// src/pages/Community/Missions/MissionCompleted.jsx
import React, { useCallback, useEffect, useState } from "react";
import {
  doc,
  onSnapshot,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db } from "../../../firebase";
import CompletionChecklistModal from "../Missions/CompletionChecklistModal";
import ConfirmModal from "../../../components/ConfirmModal";
import "./css/MissionCompleted.css";
import { toast } from "react-toastify";
import { enqueueXPEvent } from "../../../services/xpService";

export default function MissionCompleted({
  basePath,
  label = "🏁 Mark as completed",
  doneLabel = "✓ Completed",
  className = "",
  showCount = false,
  mission,
}) {
  const [uid, setUid] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [count, setCount] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // auth
  useEffect(() => {
    const auth = getAuth();
    return onAuthStateChanged(auth, (u) => setUid(u?.uid || null));
  }, []);

  // my completion
  useEffect(() => {
    if (!uid || !Array.isArray(basePath) || basePath.length === 0) return;
    const compRef = doc(db, ...basePath, "completions", uid);
    return onSnapshot(compRef, (snap) => {
      setCompleted(snap.exists() ? snap.data()?.completed === true : false);
    });
  }, [uid, basePath]);

  // count (optional) — now reads top-level completedCount with fallback to legacy stats.completedCount
  useEffect(() => {
    if (!showCount || !Array.isArray(basePath) || basePath.length === 0) return;
    const entityRef = doc(db, ...basePath);
    return onSnapshot(entityRef, (snap) => {
      const v = snap.data();
      const topLevel = v?.completedCount ?? null;
      const legacy = v?.stats?.completedCount ?? null; // backward compat read
      setCount(
        typeof topLevel === "number"
          ? topLevel
          : typeof legacy === "number"
          ? legacy
          : 0
      );
    });
  }, [showCount, basePath]);

  const onOpen = () => {
    if (!uid) {
      toast.info("Please sign in to mark as completed");
      return;
    }
    setOpen(true);
  };

  const onSubmit = useCallback(
    async ({ checklist, meta }) => {
      if (!uid || !Array.isArray(basePath) || basePath.length === 0) return;
      setSaving(true);
      setError("");

      const entityRef = doc(db, ...basePath);
      const compRef = doc(db, ...basePath, "completions", uid);

       const missionId = basePath?.[1] || null;
      let justCompleted = false;
      let ownerUid = null;
      const uniq = () => `${Date.now()}_${Math.random().toString(36).slice(2,10)}`;
      async function enqueueMissionCompleteXP() {
        if (!missionId) return;
        try {
          await enqueueXPEvent({
            uid,
            type: "MISSION_COMPLETE",
            amount: 3,               // completer +3
            amountReceiver: ownerUid && ownerUid !== uid ? 1 : 0, // owner +1
            eventId: `mission_complete:${missionId}:${uid}:${uniq()}`,
            meta: {
              postId: missionId,
              postOwnerUid: ownerUid || null,
              postCollection: "missions",
            },
          });
        } catch {/* best-effort */}
    }

      try {
        await runTransaction(db, async (tx) => {
          const [compSnap, entSnap] = await Promise.all([
            tx.get(compRef),
            tx.get(entityRef),
          ]);
          const already =
            compSnap.exists() && compSnap.data()?.completed !== false;

          // read current count from top-level with fallback to legacy stats
          const entData = entSnap.data() || {};
                    ownerUid =
            entData.createdBy ||
            entData.ownerUid ||
            entData.uid ||
            entData.userId ||
            null;
          const current =
            typeof entData.completedCount === "number"
              ? entData.completedCount
              : typeof entData?.stats?.completedCount === "number"
              ? entData.stats.completedCount
              : 0;

          if (!already) {
            tx.set(
              compRef,
              {
                uid,
                completed: true,
                completedAt: serverTimestamp(),
                checklist: {
                  primary: checklist?.primary ?? [],
                  optional: checklist?.optional ?? [],
                },
                meta: {
                  minutesSpent: meta?.minutesSpent ?? null,
                  notes: meta?.notes ?? null,
                  evidence: Array.isArray(meta?.evidence) ? meta.evidence : [],
                },
              },
              { merge: true }
            );
            // WRITE to top-level completedCount (no longer under stats)
            tx.set(entityRef, { completedCount: current + 1 }, { merge: true });
            justCompleted = true;
          } else {
            // update only the details; count unchanged
            tx.set(
              compRef,
              {
                checklist: {
                  primary: checklist?.primary ?? [],
                  optional: checklist?.optional ?? [],
                },
                meta: {
                  minutesSpent: meta?.minutesSpent ?? null,
                  notes: meta?.notes ?? null,
                  evidence: Array.isArray(meta?.evidence) ? meta.evidence : [],
                },
              },
              { merge: true }
            );
          }
        });
        // enqueue XP only after TX confirms, and only on first completion
      if (justCompleted) await enqueueMissionCompleteXP();
      } catch (e) {
        console.error(e);
        setError("Could not save your completion. Please try again.");
      } finally {
        setSaving(false);
      }
    },
    [uid, basePath]
  );

  // Unmark transaction — decrements top-level completedCount with legacy fallback on read
  const runUnmarkTx = useCallback(async () => {
    const entityRef = doc(db, ...basePath);
    const compRef = doc(db, ...basePath, "completions", uid);
    let ownerUid = null;
    let didUnmark = false;
    const missionId = basePath?.[1] || null;
    const uniq = () => `${Date.now()}_${Math.random().toString(36).slice(2,10)}`;
    async function enqueueMissionUncompleteXP() {
      if (!missionId) return;
      try {
        await enqueueXPEvent({
          uid,
          type: "MISSION_UNCOMPLETE",
          amount: -3,              // completer −3
          amountReceiver: ownerUid && ownerUid !== uid ? -1 : 0, // owner −1
          eventId: `mission_uncomplete:${missionId}:${uid}:${uniq()}`,
          meta: {
            postId: missionId,
            postOwnerUid: ownerUid || null,
            postCollection: "missions",
          },
        });
      } catch {/* best-effort */}
    }

    await runTransaction(db, async (tx) => {
      const [compSnap, entSnap] = await Promise.all([
        tx.get(compRef),
        tx.get(entityRef),
      ]);

      const entData = entSnap.data() || {};
       ownerUid =
        entData.createdBy ||
        entData.ownerUid ||
        entData.uid ||
        entData.userId ||
        null;
      const current =
        typeof entData.completedCount === "number"
          ? entData.completedCount
          : typeof entData?.stats?.completedCount === "number"
          ? entData.stats.completedCount
          : 0;

      if (compSnap.exists()) {
        tx.delete(compRef);
        tx.set(
          entityRef,
          { completedCount: Math.max(0, current - 1) },
          { merge: true }
        );
         didUnmark = true;
      }
    });
    if (didUnmark) await enqueueMissionUncompleteXP();
  }, [uid, basePath]);

  const requestUnmark = () => setConfirmOpen(true);

  const confirmUnmark = async () => {
    setSaving(true);
    setError("");
    try {
      await runUnmarkTx();
    } catch (e) {
      console.error(e);
      setError("Could not unmark. Please try again.");
    } finally {
      setSaving(false);
      setConfirmOpen(false);
    }
  };

  const isDone = !!uid && !!completed;
  const disabled = saving;

  useEffect(() => {
    if (!uid) {
      setCompleted(false);
      setOpen(false);
      setConfirmOpen(false);
    }
  }, [uid]);

  return (
    <div className={`msn-complete ${className}`}>
      <button
        type="button"
        className={`msn-action bookmark-btn ${className} ${isDone ? "is-done" : ""} ${(!uid || saving) ? "is-disabled" : ""}`}
        onClick={isDone ? requestUnmark : onOpen}
        disabled={saving}
        aria-disabled={!uid || saving}
        title={
          !uid
            ? "Sign in to track completion"
            : isDone
            ? "Remove completion"
            : "Mark as completed"
        }
        onKeyDown={(e) => {
          if ((e.key === "Enter" || e.key === " ") && !e.shiftKey) {
            e.preventDefault();
            if (saving) return;
            isDone ? requestUnmark() : onOpen();
          }
        }}
      >
        {saving
          ? "⏳ Saving…"
          : isDone
          ? "🏁 Completed"
          : "🏁 Mark as completed"}{" "}
        · {count ?? "-"}
      </button>

      {error && <span className="msn-engage-error">{error}</span>}

      <CompletionChecklistModal
        open={!isDone && open}
        onClose={() => setOpen(false)}
        onSubmit={onSubmit}
        basePath={basePath}
        missionName={mission?.missionName}
        honestyNote="This is for your personal progress tracking. It’s private and not visible to other users—please be honest."
      />

      <ConfirmModal
        open={confirmOpen}
        title="Unmark this mission?"
        message="This is your personal progress tracker. You can re-mark it later if needed."
        confirmText="Yes, unmark"
        cancelText="Cancel"
        onConfirm={confirmUnmark}
        onCancel={() => setConfirmOpen(false)}
        busy={saving}
      />
    </div>
  );
}
